import React, { useMemo, useState } from 'react';
import { GtmComponent, GtmProduct, GtmProductFeature } from '../../../types/go-to-market';
import { Product } from '../../../types/product';
import ArchitectureComponentList from './ArchitectureComponentList';
import ArchitectureProductList from './ArchitectureProductList';
import ArchitectureFeatureList from './ArchitectureFeatureList';

const flexSpanMap: Record<number, string> = {
  1: 'flex-[1]',
  2: 'flex-[2]',
  3: 'flex-[3]',
};

const ArchitectureComponentMap: React.FC<{
  components: GtmComponent[],
  productMapMaster: Record<Product['id'], Product>,
  solutionDetailLoading?: boolean,
  bgColor?: string,
  columnSpan?: [number, number],
}> = ({
  components,
  productMapMaster,
  solutionDetailLoading = false,
  bgColor = 'bg-cisco-blue-5',
  columnSpan = [1, 2]
}) => {
  const architectureMaps = useMemo(() => {
    const productsMap = new Map<Product['id'], GtmProduct>();
    const componentMap = new Map<GtmComponent['id'], Set<Product['id']>>();
    let colorIndex = 0;
  
    if (components) {
      for (const component of (components ?? [])) {
        const features = component?.product_feature ?? [];
        for (const feature of features) {
          const productId = typeof feature?.product === 'object' ? feature?.product.id : feature?.product;

          if (!productMapMaster[productId]) continue;

          // add product to map
          if (!productsMap.has(productId)) {
            productsMap.set(productId, {
              ...productMapMaster[productId],
              colorIdx: colorIndex++,
              features: new Map<GtmComponent['id'], GtmProductFeature[]>()
            });
          }
          
          // add feature to map based on product and component
          const product = productsMap.get(productId);
          if (product) {
            if (!product.features.has(component.id)) {
              product.features.set(component.id, []);
            }
            product.features.get(component.id).push({
              id: feature.id,
              name: feature.name,
              description: feature.description,
              product: feature.product,
            });
          }

          // Build reverse lookup: component -> products
          if (!componentMap.has(component.id)) {
            componentMap.set(component.id, new Set());
          }
          componentMap.get(component.id).add(productId);
        }
      }
    }
    return {
      products: productsMap,
      components: componentMap
    };
  }, [components, productMapMaster]);

  // The component currently hovered over
  const [hoveredComponentId, setHoveredComponentId] = useState<GtmComponent['id'] | null>(null);

  // The component currently pinned (clicked on)
  const [pinnedComponentId, setPinnedComponentId] = useState<GtmComponent['id'] | null>(null);
  const pinnedProductIds: Set<GtmProduct['id']> | null = useMemo(() => {
    if (!pinnedComponentId || !architectureMaps?.components) {
      return null;
    }
    return architectureMaps.components.get(pinnedComponentId) || null;
  }, [pinnedComponentId, architectureMaps]);
  
  const lftColumnSpan = flexSpanMap[columnSpan[0]] ?? 'flex-[1]';
  const rgtColumnSpan = flexSpanMap[columnSpan[1]] ?? 'flex-[2]';

  return (
    <div className={`${bgColor} px-4 pb-6`}>
      <div className="flex flex-row gap-6 w-full">
        <div className={lftColumnSpan}>
          <ArchitectureComponentList
            components={components ?? []}
            setHoveredComponent={(id) => setHoveredComponentId(id)}
            hoveredComponent={hoveredComponentId}
            setPinnedComponent={setPinnedComponentId}
            pinnedComponent={pinnedComponentId}
          />
        </div>
        <div className={rgtColumnSpan}>
          <ArchitectureProductList
            products={architectureMaps.products}
            productsLoading={solutionDetailLoading}
            hoveredProducts={architectureMaps.components.get(hoveredComponentId) ?? new Set()}
            pinnedProducts={pinnedProductIds}
          />
        </div>
      </div>
      <ArchitectureFeatureList
        products={architectureMaps.products}
        pinnedProducts={pinnedProductIds}
        component={pinnedComponentId}
      />
    </div>
  )
}
export default ArchitectureComponentMap;